import MarketingConsentSelect from "@/components/contact/MarketingConsentSelect";
import ContactsEmailAutoComplete from "@/components/form/components/ContactsEmailAutoComplete";
import { ContactsPhoneNumberAutoComplete } from "@/components/form/components/ContactsPhoneNumberAutoComplete";
import HiddenInput from "@/components/form/components/HiddenInput";
import LabelWithPopover from "@/components/form/labels/LabelWithPopover";
import { datePickerFormItemProps, datePickerStandardProps } from "@/utils/componentUtils";
import { phoneNumberNormalizeFunction, resolveFormValidationError } from "@/utils/formUtils";
import { numberOrZero, parseBirthDateFromPin, toDate } from "@/utils/utils";
import { regexPatterns, validationFunctions, validations } from "@/utils/validationUtils";
import { Alert, Card, Checkbox, Col, DatePicker, Divider, Form, Input, Row } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { Rule } from "antd/lib/form";
import dayjs, { isDayjs } from "dayjs";
import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import t from "../../../../../../../app/i18n";
import { rowGutter } from "../../../../../../../common/constants";
import { FieldConstraintViolation } from "../../../../../../../common/types";
import { useIsCurrentAgentTypePlus } from "../../../../../../auth/hooks";
import ClientDrawerForm from "../../../../../../client/components/drawers/ClientDrawerForm";
import ClientRepresentativeSearchForm from "../../../../../../client/components/search/ClientRepresentativeSearchForm";
import ClientSearchInput from "../../../../../../client/components/search/ClientSearchInput";
import { ClientFormStage, ClientFormType, ClientSearchActionType, ClientType } from "../../../../../../client/enums";
import { useClientSearch } from "../../../../../../client/hooks";
import { Client, LegalClient, NaturalClient } from "../../../../../../client/types";
import { InstitutionEnum } from "../../../../../../institution/enums";
import { resolveClientFormTypeIdentifierName } from "../../../../utils";
import { TravelInsuranceType } from "../../../enums";
import { TravelCalc, TravelFormClients, TravelGenForm, TravelGenFormInsuredClient } from "../../../types";

const FamilyInsuranceForm = ({ insuredClientsCount }: { insuredClientsCount: number }) => {
  const { t } = useTranslation();

  return (
    <>
      <Row gutter={rowGutter}>
        <Col span={16}>
          <Alert type="info" showIcon title={t("calc.travel.helpers.axaAssistanceYearFamilyInsuranceDesc")} />
        </Col>
      </Row>
      {Array.from({ length: insuredClientsCount }, (_, i) => i).map((clientIndex) => (
        <HiddenInput key={clientIndex} name={["clientsData", "insuredClients", clientIndex, "birthDate"]} />
      ))}
    </>
  );
};

const LegalFamilyInsuranceForm = ({ insuredClientsCount }: { insuredClientsCount: number }) => {
  const { t } = useTranslation();

  return (
    <>
      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name={["clientsData", "insuredClientForFamilyInsurance", "birthDate"]}
            label={t("calc.travel.attrs.clientsData.insuredClientForFamilyInsurance.birthDate")}
            rules={[validations.notNull, validations.notPresentAndFuture]}
            {...datePickerFormItemProps}
          >
            <DatePicker {...datePickerStandardProps} showNow={false} maxDate={dayjs().subtract(1, "day")} />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name={["clientsData", "insuredClientForFamilyInsurance", "firstName"]}
            label={t("calc.travel.attrs.clientsData.insuredClientForFamilyInsurance.firstName")}
            rules={[
              validations.notBlank,
              validations.size(1, 255),
              validations.pattern(regexPatterns.uppercaseWordRegex),
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name={["clientsData", "insuredClientForFamilyInsurance", "lastName"]}
            label={t("calc.travel.attrs.clientsData.insuredClientForFamilyInsurance.lastName")}
            rules={[
              validations.notBlank,
              validations.size(1, 255),
              validations.pattern(regexPatterns.uppercaseWordRegex),
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={rowGutter}>
        <Col span={16}>
          <Alert type="info" showIcon title={t("calc.travel.helpers.axaAssistanceYearFamilyInsuranceLegalDesc")} />
        </Col>
      </Row>
      {Array.from({ length: insuredClientsCount }, (_, i) => i).map((clientIndex) => (
        <HiddenInput key={clientIndex} name={["clientsData", "insuredClients", clientIndex, "birthDate"]} />
      ))}
    </>
  );
};

const DefaultInsuredClientForm = ({
  selectedInstitutionEnum,
  calcData,
  handleInsuredClientPinChange,
  policyHolderIsAlsoInsured,
}: {
  selectedInstitutionEnum?: InstitutionEnum;
  calcData: TravelCalc;
  handleInsuredClientPinChange: (pin: string, index: number) => void;
  policyHolderIsAlsoInsured: boolean;
}) => {
  const { t } = useTranslation();

  const { insuredClients } = calcData.clientsData;

  const unionYearHealthInsuranceDiscount =
    selectedInstitutionEnum === InstitutionEnum.UNION &&
    calcData.generalData.insuranceType === TravelInsuranceType.YEAR &&
    calcData.discountsData?.unionHealthInsuranceClient;

  return Array.from({ length: numberOrZero(insuredClients?.length) }, (_, i) => i).map((clientIndex) => (
    <React.Fragment key={clientIndex}>
      <Row gutter={rowGutter}>
        <Col className="bold-text margin-bottom-tiny">
          {t("calc.travel.sections.insuredClient", { number: clientIndex + 1 })}
        </Col>
      </Row>
      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name={["clientsData", "insuredClients", clientIndex, "birthDate"]}
            label={t("calc.travel.attrs.clientsData.insuredClients.birthDate")}
            rules={[validations.notNull, validations.notPresentAndFuture]}
            {...datePickerFormItemProps}
          >
            <DatePicker {...datePickerStandardProps} showNow={false} maxDate={dayjs().subtract(1, "day")} disabled />
          </Form.Item>
        </Col>

        {unionYearHealthInsuranceDiscount ? (
          <Col span={colSpan}>
            <Form.Item
              name={["clientsData", "insuredClients", clientIndex, "personalIdentificationNumber"]}
              label={t("calc.travel.attrs.clientsData.insuredClients.personalIdentificationNumber")}
              rules={[
                validations.notBlank,
                validations.pin,
                insuredClientPinForBirthDateRule(insuredClients?.[clientIndex]?.birthDate),
                insuredClientNoDuplicatePinRule(clientIndex),
              ]}
              validateFirst
            >
              <Input
                disabled={clientIndex === 0 && policyHolderIsAlsoInsured}
                onChange={(e) => handleInsuredClientPinChange(e.target.value, clientIndex)}
              />
            </Form.Item>
          </Col>
        ) : undefined}

        <Col span={colSpan}>
          <Form.Item
            name={["clientsData", "insuredClients", clientIndex, "firstName"]}
            label={t("calc.travel.attrs.clientsData.insuredClients.firstName")}
            rules={[
              validations.notBlank,
              validations.size(1, 255),
              validations.pattern(regexPatterns.uppercaseWordRegex),
            ]}
          >
            <Input disabled={clientIndex === 0 && policyHolderIsAlsoInsured} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["clientsData", "insuredClients", clientIndex, "lastName"]}
            label={t("calc.travel.attrs.clientsData.insuredClients.lastName")}
            rules={[
              validations.notBlank,
              validations.size(1, 255),
              validations.pattern(regexPatterns.uppercaseWordRegex),
            ]}
          >
            <Input disabled={clientIndex === 0 && policyHolderIsAlsoInsured} />
          </Form.Item>
        </Col>

        {selectedInstitutionEnum === InstitutionEnum.UNION &&
          calcData.generalData.insuranceType === TravelInsuranceType.SHORT_TERM &&
          insuredClients?.[clientIndex]?.discountType && (
            <Col span={colSpan}>
              <Form.Item
                name={["clientsData", "insuredClients", clientIndex, "discountIdentifier"]}
                label={
                  <LabelWithPopover
                    label={t("calc.travel.attrs.clientsData.insuredClients.discountIdentifier")}
                    popoverTitle={t("calc.travel.helpers.discount.title", {
                      type: t("calc.travel.enums.clientDiscountType." + insuredClients[clientIndex]?.discountType),
                    })}
                    popoverContent={
                      <p style={{ maxWidth: "300px" }}>
                        {t("calc.travel.helpers.discount.type." + insuredClients[clientIndex]?.discountType)}
                      </p>
                    }
                  />
                }
                rules={[validations.notBlank, validations.size(1, 255)]}
              >
                <Input />
              </Form.Item>
            </Col>
          )}
      </Row>
    </React.Fragment>
  ));
};

interface ClientsStagesState {
  readonly policyHolder?: ClientFormStage;
  readonly representative?: ClientFormStage;
}

const getClientFormStage = (type: ClientFormType, clientStages: ClientsStagesState): ClientFormStage | undefined => {
  switch (type) {
    case ClientFormType.POLICY_HOLDER:
      return clientStages.policyHolder;
    case ClientFormType.REPRESENTATIVE:
      return clientStages.representative;
    default:
      return undefined;
  }
};

const getClientFromProps = (type: ClientFormType, clients: TravelFormClients) => {
  switch (type) {
    case ClientFormType.POLICY_HOLDER:
      return clients.policyHolder;
    case ClientFormType.REPRESENTATIVE:
      return clients.representative;
    default:
      return undefined;
  }
};

const resolveClientTypeByClientFormType = (type: ClientFormType): ClientType | undefined => {
  switch (type) {
    case ClientFormType.POLICY_HOLDER:
      return undefined;
    case ClientFormType.REPRESENTATIVE:
      return ClientType.NATURAL;
    default:
      return undefined;
  }
};

const colSpan = 4;

const policyHolderIsAlsoInsuredBirthDateRule = (
  insuredClientBirthDate?: string,
  policyHolderBirthDate?: string,
): Rule => ({
  validator: (_, value) =>
    value &&
    policyHolderBirthDate &&
    isDayjs(toDate(insuredClientBirthDate)) &&
    !toDate(policyHolderBirthDate)?.isSame(insuredClientBirthDate, "day")
      ? Promise.reject(t("calc.travel.validations.incorrectBirtDateFromPolicyHolder"))
      : Promise.resolve(),
});

const insuredClientPinForBirthDateRule = (birthDate?: string): Rule => ({
  validator: (_, value) =>
    value &&
    validationFunctions.validatePin(value) &&
    isDayjs(toDate(birthDate)) &&
    !toDate(parseBirthDateFromPin(value))?.isSame(birthDate, "day")
      ? Promise.reject(t("calc.travel.validations.incorrectPinForBirthDate"))
      : Promise.resolve(),
});

const insuredClientNoDuplicatePinRule =
  (index: number): Rule =>
  ({ getFieldValue }) => ({
    validator: (_, value) => {
      if (value) {
        const insuredClients = [
          ...(getFieldValue(["clientsData", "insuredClients"]) || []),
        ] as TravelGenFormInsuredClient[];

        return insuredClients.some(
          (insuredClient, i) => index !== i && insuredClient?.personalIdentificationNumber === value,
        )
          ? Promise.reject(t("calc.travel.validations.duplicatePin"))
          : Promise.resolve();
      }
      return Promise.resolve();
    },
  });

interface Props {
  calcData: TravelCalc;
  clients: TravelFormClients;
  clientsViolationErrors: Map<ClientFormType, FieldConstraintViolation[]>;
  selectedInstitutionEnum?: InstitutionEnum;
  onPolicyHolderIsAlsoInsuredChange: (event: CheckboxChangeEvent) => void;
  onClientChange: (type: ClientFormType, client?: Partial<Client>) => void;
  onClientViolationErrorsChange: (type: ClientFormType, violations?: FieldConstraintViolation[]) => void;
}

export const TravelGenClientsDataSection = ({ calcData, clients, selectedInstitutionEnum, ...props }: Props) => {
  const { t } = useTranslation();

  const clientSearch = useClientSearch();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const form = Form.useFormInstance<TravelGenForm>();
  const policyHolderIsAlsoInsured = Form.useWatch(["clientsData", "policyHolderIsAlsoInsured"], form);

  const [processedClientFormType, setProcessedClientFormType] = useState<ClientFormType>();
  const [clientFormOpen, setClientFormOpen] = useState<boolean>(false);
  const [clientStages, setClientStages] = useState<ClientsStagesState>({
    policyHolder: clients.policyHolder ? ClientFormStage.EXISTING : undefined,
    representative:
      clients.representative &&
      clients.policyHolder?.type === ClientType.LEGAL &&
      ((clients.policyHolder as LegalClient)?.representatives || []).some(
        (r) => r.representative.identifier === clients.representative?.identifier,
      )
        ? ClientFormStage.EXISTING
        : undefined,
  });

  const getClientFormIdentifier = (type: ClientFormType): string => {
    switch (type) {
      case ClientFormType.POLICY_HOLDER:
        return form.getFieldValue(["clientsData", "policyHolderIdentifier"]);
      case ClientFormType.REPRESENTATIVE:
        return form.getFieldValue(["clientsData", "representativeIdentifier"]);
      default:
        return "";
    }
  };

  const setClientFormStage = (type: ClientFormType, stage?: ClientFormStage) => {
    switch (type) {
      case ClientFormType.POLICY_HOLDER:
        setClientStages({ ...clientStages, policyHolder: stage });
        break;
      case ClientFormType.REPRESENTATIVE:
        setClientStages({ ...clientStages, representative: stage });
        break;
    }
  };

  useEffect(() => {
    return () => {
      clientSearch.onResultDelete();
    };
  }, []);

  useEffect(() => {
    const { result } = clientSearch;
    const isMatchingType =
      processedClientFormType && result.keyword === getClientFormIdentifier(processedClientFormType);

    if (!isMatchingType) {
      return;
    }

    const { data } = result;

    if (!data) {
      setClientFormStage(processedClientFormType, ClientFormStage.NEW);
      return;
    }

    setClientFormOpen(true);
    setClientFormStage(processedClientFormType, ClientFormStage.EXISTING);

    const isNaturalOrSelfEmployedClient = data.type === ClientType.NATURAL || data.type === ClientType.SELF_EMPLOYED;

    const clientPayload = isNaturalOrSelfEmployedClient
      ? {
          ...data,
          identityCardNumber: undefined,
          previousIdentityCardNumber: (data as NaturalClient).identityCardNumber,
        }
      : data;

    props.onClientChange(processedClientFormType, clientPayload);
  }, [clientSearch.result]);

  const handleInsuredClientPinChange = (pin: string, index: number) => {
    if (calcData.generalData.insuranceType === TravelInsuranceType.CANCELLATION) {
      const insuredClients = [...form.getFieldValue(["clientsData", "insuredClients"])];

      insuredClients[index] = {
        ...insuredClients[index],
        birthDate: validationFunctions.validatePin(pin) ? parseBirthDateFromPin(pin) : "",
      } as TravelGenFormInsuredClient;

      form.setFieldsValue({ clientsData: { insuredClients } });
    }
  };

  const handleClientSearchSubmit = (value: string, type: ClientFormType) => {
    form
      .validateFields([["clientsData", resolveClientFormTypeIdentifierName(type)]])
      .then(() => clientSearch.onSearch({ keyword: value, clientType: resolveClientTypeByClientFormType(type) }))
      .catch(resolveFormValidationError);
  };

  const handleClientSearchChange = (value: string, type: ClientFormType) => {
    if (getClientFormStage(type, clientStages)) {
      setClientFormStage(type);
    }
    if (getClientFromProps(type, clients)) {
      props.onClientChange(type);
    }
    if (props.clientsViolationErrors.has(type)) {
      props.onClientViolationErrorsChange(type);
    }
    handleClientSearchSubmit(value, type);
  };

  const handleClientSearchActionClick = (type: ClientFormType, actionType: ClientSearchActionType) => {
    switch (actionType) {
      case ClientSearchActionType.CREATE:
      case ClientSearchActionType.UPDATE:
        setProcessedClientFormType(type);
        setClientFormOpen(true);
        break;
      case ClientSearchActionType.DELETE:
        form.setFieldsValue({ clientsData: { [resolveClientFormTypeIdentifierName(type)]: null } });
        setClientFormStage(type);
        props.onClientViolationErrorsChange(type);
        props.onClientChange(type);
        break;
    }
  };

  const handleClientFormSubmit = (client: Client, clientFormType: ClientFormType) => {
    setClientFormOpen(false);
    setProcessedClientFormType(undefined);
    setClientFormStage(clientFormType, ClientFormStage.SELECTED);
    props.onClientViolationErrorsChange(clientFormType);
    props.onClientChange(clientFormType, client);
  };

  const { insuredClients } = calcData.clientsData;

  const axaAssistanceYearFamilyInsurance =
    selectedInstitutionEnum === InstitutionEnum.AXA_ASSISTANCE &&
    calcData.generalData.insuranceType === TravelInsuranceType.YEAR &&
    calcData.discountsData?.familyInsurance;

  const generaliYearInsurance =
    selectedInstitutionEnum === InstitutionEnum.GENERALI &&
    calcData.generalData.insuranceType === TravelInsuranceType.YEAR;

  const clientSearchProps = {
    processedType: processedClientFormType,
    violationErrors: props.clientsViolationErrors,
    inProgress: clientSearch.inProgress,
    inputColSpan: 5,
    clientNameColSpan: 6,
    onActionClick: handleClientSearchActionClick,
    onFocus: setProcessedClientFormType,
    onSearch: handleClientSearchSubmit,
    onChange: handleClientSearchChange,
  };

  return (
    <>
      <Card type="inner" className="card-box" title={t("calc.travel.sections.policyHolder")}>
        <Row gutter={rowGutter}>
          <ClientSearchInput<ClientFormType>
            {...clientSearchProps}
            formItemProps={{
              name: ["clientsData", "policyHolderIdentifier"],
              label: t("calc.travel.attrs.clientsData.policyHolderPinCrn"),
              rules: [validations.notBlank, validations.pinOrCrn],
              validateFirst: true,
            }}
            formStage={clientStages.policyHolder}
            formType={ClientFormType.POLICY_HOLDER}
            client={clients.policyHolder}
          />

          {axaAssistanceYearFamilyInsurance ? (
            <HiddenInput name={["clientsData", "policyHolderIsAlsoInsured"]} initialValue={false} />
          ) : (
            <Col span={colSpan * 2}>
              <Form.Item
                name={["clientsData", "policyHolderIsAlsoInsured"]}
                className="form-item-without-label"
                valuePropName="checked"
                rules={[
                  policyHolderIsAlsoInsuredBirthDateRule(
                    insuredClients?.[0]?.birthDate,
                    clients.policyHolder?.type === ClientType.NATURAL
                      ? (clients.policyHolder as NaturalClient).birthDate
                      : undefined,
                  ),
                ]}
                initialValue={false}
              >
                <Checkbox
                  disabled={!clients.policyHolder || clients.policyHolder.type !== ClientType.NATURAL}
                  onChange={props.onPolicyHolderIsAlsoInsuredChange}
                >
                  {t("calc.travel.attrs.clientsData.policyHolderIsAlsoInsured")}
                </Checkbox>
              </Form.Item>
            </Col>
          )}
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <ContactsEmailAutoComplete
              formItemProps={{
                name: ["clientsData", "policyHolderEmail"],
                label: t("calc.travel.attrs.clientsData.policyHolderEmail"),
                rules: [validations.notBlank, validations.size(1, 254), validations.email],
              }}
              contacts={clients.policyHolder?.contacts}
            />
          </Col>

          <Col span={colSpan}>
            <ContactsPhoneNumberAutoComplete
              formItemProps={{
                name: ["clientsData", "policyHolderPhone"],
                label: t("calc.travel.attrs.clientsData.policyHolderPhone"),
                rules: [validations.notBlank, validations.mobilePhoneNumber()],
                normalize: phoneNumberNormalizeFunction,
              }}
              contacts={clients.policyHolder?.contacts}
            />
          </Col>

          <Col span={colSpan}>
            <MarketingConsentSelect
              formItemProps={{
                name: ["clientsData", "policyHolderMarketingConsent"],
                label: isTopAgentPlus ? (
                  <LabelWithPopover
                    popoverContent={
                      <div style={{ maxWidth: 500, fontSize: 12 }}>
                        <Trans i18nKey="contact.helpers.marketingConsentHint" />
                      </div>
                    }
                    label={t("contact.enums.marketingConsent._label")}
                  />
                ) : (
                  t("contact.enums.marketingConsent._label")
                ),
                rules: [validations.notNull],
              }}
            />
          </Col>
        </Row>

        <ClientRepresentativeSearchForm
          form={form}
          policyHolder={clients.policyHolder}
          searchInputProps={{
            ...clientSearchProps,
            formStage: clientStages.representative,
            formType: ClientFormType.REPRESENTATIVE,
            client: clients.representative,
          }}
          colSpan={colSpan}
        />

        <Divider className="divider-subheader">{t("calc.travel.sections.insuredClients")}</Divider>

        {axaAssistanceYearFamilyInsurance ? (
          !clients.policyHolder || clients.policyHolder.type === ClientType.NATURAL ? (
            <FamilyInsuranceForm insuredClientsCount={calcData.clientsData.insuredClientsCount} />
          ) : (
            <LegalFamilyInsuranceForm insuredClientsCount={calcData.clientsData.insuredClientsCount} />
          )
        ) : (
          <DefaultInsuredClientForm
            selectedInstitutionEnum={selectedInstitutionEnum}
            calcData={calcData}
            handleInsuredClientPinChange={handleInsuredClientPinChange}
            policyHolderIsAlsoInsured={policyHolderIsAlsoInsured}
          />
        )}

        {generaliYearInsurance ? (
          <Row gutter={rowGutter}>
            <Col span={12}>
              <Alert type="info" showIcon title={t("calc.travel.helpers.generaliYearInsuranceDesc")} />
            </Col>
          </Row>
        ) : undefined}
      </Card>

      <ClientDrawerForm<ClientFormType>
        open={clientFormOpen}
        client={processedClientFormType ? getClientFromProps(processedClientFormType, clients) : undefined}
        initialClientType={
          processedClientFormType ? resolveClientTypeByClientFormType(processedClientFormType) : undefined
        }
        initialIdentifier={processedClientFormType ? getClientFormIdentifier(processedClientFormType) : undefined}
        requireIdCardNumber
        formType={processedClientFormType}
        violationErrors={props.clientsViolationErrors}
        onFormSubmit={handleClientFormSubmit}
      />
    </>
  );
};
